import * as React from 'react';
import { noop } from 'react-financial-charts';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { InteractiveStraightLine } from './Wrapper';
import { getValueFromOverride } from '@/utils/utils';
import type { VertLineProps } from './InteractiveVerticalLine';
import type { CoordDragOverride, HorzLineRenderNode, MoreProps, VertLineRenderNode } from '../../../types/commontypes';
import { toHorzVertLineRFCProps, RFC_RENDER_MODE } from '@/utils/shapePropsToAppearance';

type VertLineDragOverride = CoordDragOverride;

interface VertLineState {
  override?: VertLineDragOverride;
  trends?: unknown;
  lineInteractiveCursor?: string;
}

export class _VerticalLine extends React.Component<VertLineProps, VertLineState> {
  public static defaultProps = {
    onStart: noop,
    onSelect: noop,
    currentPositionStroke: '#1e53e6',
    currentPositionstrokeOpacity: 1,
    currentPositionStrokeWidth: 1,
    currentPositionRadius: 0,
    shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
    hoverText: {
      ...HoverTextNearMouse.defaultProps,
      enable: true,
      bgHeight: 'auto',
      bgWidth: 'auto',
      text: 'Click to select object',
      selectedText: '',
    },
    trends: [],
  };
  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved = false;
  private saveNodeType: <T>(idx: number | string) => React.Ref<T>;

  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: VertLineProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('trends').bind(this);

    this.state = {};
  }

  public render() {
    const { trends, nonInteractiveStraightLine, overrideData } = this.props;
    const { override } = this.state;
    return (
      <g>
        {trends.map((each, idx) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toHorzVertLineRFCProps(each as HorzLineRenderNode | VertLineRenderNode) as Record<
            string,
            unknown
          > & {
            strokeStyle: string;
            strokeWidth: number;
            strokeDasharray: string;
            edgeStroke: string;
            edgeFill: string;
            edgeStrokeWidth: number;
            r: number;
          };
          const shouldRender = overrideData && overrideData.uuid === each.uuid;
          const eachStartEnd = each as typeof each & { start?: [number, number]; end?: [number, number] };
          if (shouldRender || !eachStartEnd.start || !eachStartEnd.end) return null;

          return (
            <InteractiveStraightLine
              key={each.uuid ?? idx}
              selected={(each as unknown as { selected?: boolean }).selected}
              x1Value={getValueFromOverride(override, each.uuid, 'x1Value', eachStartEnd.start[0])}
              y1Value={getValueFromOverride(override, each.uuid, 'y1Value', eachStartEnd.start[1])}
              x2Value={getValueFromOverride(override, each.uuid, 'x2Value', eachStartEnd.end[0])}
              y2Value={getValueFromOverride(override, each.uuid, 'y2Value', eachStartEnd.end[1])}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={RFC_RENDER_MODE[each.type] as 'XLINE' | 'RAY' | 'LINE'}
              strokeStyle={eachAppearance.strokeStyle}
              strokeWidth={eachAppearance.strokeWidth}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeDasharray={eachAppearance.strokeDasharray as never}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              edgeStrokeWidth={eachAppearance.edgeStrokeWidth}
              r={eachAppearance.r}
              interactiveCursorClass={'react-financial-charts-pointer-cursor'}
              extendLeft={false}
              extendRight={false}
            />
          );
        })}

        {/*While dragging*/}
        {nonInteractiveStraightLine &&
          overrideData &&
          (() => {
            const matchingTrend = trends.find(t => t.uuid === overrideData.uuid);
            if (!matchingTrend) return null;

            const activeAppearance = toHorzVertLineRFCProps(
              matchingTrend as HorzLineRenderNode | VertLineRenderNode,
            ) as Record<string, unknown> & {
              strokeStyle: string;
              strokeWidth: number;
              strokeDasharray: string;
            };

            return (
              <InteractiveStraightLine
                ref={this.saveNodeType('line')}
                selected={true}
                x1Value={overrideData.x1Value}
                y1Value={overrideData.y1Value}
                x2Value={overrideData.x2Value}
                y2Value={overrideData.y2Value}
                type={'LINE'}
                strokeStyle={activeAppearance.strokeStyle}
                strokeWidth={activeAppearance.strokeWidth}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                strokeDasharray={activeAppearance.strokeDasharray as never}
                interactiveCursorClass={'react-financial-charts-pointer-cursor'}
                extendLeft={true}
                extendRight={true}
              />
            );
          })()}
      </g>
    );
  }

  componentDidUpdate(prevProps: VertLineProps) {
    const { yCoordinateClicked, onStart } = this.props;

    if (yCoordinateClicked !== prevProps.yCoordinateClicked && yCoordinateClicked !== undefined) {
      onStart(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
        {} as React.MouseEvent,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        yCoordinateClicked.value as unknown as [number, number],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        {} as unknown as MoreProps,
      );
    }
  }
}
